import { Candidate } from '~/components/UserList';
import { useCandidates } from '~/hooks/api/useCandidates';
import {
  useAssignPublisherAsLead,
  useRemoveAllTurnAssignments,
  useToggleTurnAssignment,
} from '~/hooks/api/useTurnAssignment';
import Grid from '@mui/material/Grid';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import TurnIssuesButton from '~/components/TurnIssuesButton';
import AssignedPublishers from '../AssignedPublishers';
import UnassignedPublishers from '../UnassignedPublishers';
import SyncStatus from './SyncStatus';
import messages from './messages';

const Layout = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: 1,
  borderTop: `1px solid ${theme.palette.divider}`,
  '& > *': {
    flex: 1,
  },
  '& > * + *': {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
}));

const Wrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

type CandidateAssignmentProps = {
  turnId: number;
  shiftId: number;
  requiredBrothers: number;
};

const CandidateAssignment = (props: CandidateAssignmentProps) => {
  const { data } = useCandidates({
    id: props.turnId,
  });
  const toggleTurnAssignmentMutation = useToggleTurnAssignment();
  const removeAllAssignmentsMutation = useRemoveAllTurnAssignments();
  const assignPublisherAsLeadMutation = useAssignPublisherAsLead();
  const handleToggleApplicationAssignment = (applicationId: number) => {
    toggleTurnAssignmentMutation.mutate({
      id: props.turnId,
      shiftApplicationId: applicationId,
      shiftId: props.shiftId,
    });
  };
  const assignedCandidates = data?.filter((candidate) => candidate.assignment);
  const appliedCandidates = data?.filter((candidate) => !candidate.assignment);
  const assignedBrothers = (
    assignedCandidates as unknown as Candidate[] | undefined
  )?.filter(
    (candidate) => candidate.application?.publisher?.gender === 'Male'
  ).length;
  const pendingMutations =
    toggleTurnAssignmentMutation.isPending ||
    removeAllAssignmentsMutation.isPending;
  return (
    <Wrapper>
      <Toolbar>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'center',
            flexGrow: 1,
          }}
        >
          <Grid size="grow">
            <TurnIssuesButton turnId={props.turnId} />
          </Grid>
          {props.requiredBrothers > 0 ? (
            <Grid>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  {...messages.brothersAssignedOfRequired}
                  values={{
                    assigned: assignedBrothers ?? 0,
                    required: props.requiredBrothers,
                  }}
                />
              </Typography>
            </Grid>
          ) : null}
          <Grid>
            <SyncStatus pendingMutations={pendingMutations} />
          </Grid>
        </Grid>
      </Toolbar>
      <Layout>
        {appliedCandidates ? (
          <UnassignedPublishers
            shiftId={props.shiftId}
            turnId={props.turnId}
            appliedCandidates={appliedCandidates as unknown as Candidate[]}
            onAssignCandidate={handleToggleApplicationAssignment}
          />
        ) : null}
        {assignedCandidates ? (
          <AssignedPublishers
            onRemoveAllAssignments={() =>
              removeAllAssignmentsMutation.mutate({
                id: props.turnId,
                shiftId: props.shiftId,
              })
            }
            assignedCandidates={assignedCandidates as unknown as Candidate[]}
            onRemoveCandidateAssignment={handleToggleApplicationAssignment}
            onSetPublisherAsLead={(applicationId) =>
              assignPublisherAsLeadMutation.mutate({
                id: props.turnId,
                shiftApplicationId: applicationId,
              })
            }
          />
        ) : null}
      </Layout>
    </Wrapper>
  );
};

export default CandidateAssignment;
